// src/pages/Institutional/ForOwners.js
import React from 'react'
import { Link as RouterLink } from 'react-router-dom'
import {
  Container,
  Box,
  Typography,
  Paper,
  Grid,
  Divider,
  Button,
  Chip
} from '@mui/material'
import {
  Star,
  Visibility,
  ContactPage,
  Speed,
  MoneyOff,
  Lightbulb,
  AddHome,
  CheckCircleOutline,
  WhatsApp,
  Email,
  Language
} from '@mui/icons-material'

const ForOwners = () => {
  const benefits = [
    {
      icon: <Visibility color='secondary' />,
      title: 'Seu anúncio aparece para quem realmente está buscando',
      description:
        'Os filtros ajudam compradores interessados a encontrar exatamente o tipo de imóvel que você está oferecendo.'
    },
    {
      icon: <Lightbulb color='secondary' />,
      title: 'Fotos, detalhes e informações organizadas',
      description:
        'Sua publicação fica clara, fácil de entender e com apresentação profissional.'
    },
    {
      icon: <ContactPage color='secondary' />,
      title: 'Contato direto com você',
      description:
        'Compradores falam com você pelo WhatsApp — sem intermediários.'
    },
    {
      icon: <MoneyOff color='secondary' />,
      title: 'Melhor visibilidade sem pagar comissão',
      description:
        'Seu anúncio pode alcançar centenas de compradores sem custos adicionais.'
    },
    {
      icon: <Speed color='secondary' />,
      title: 'Plataforma simples e rápida',
      description: 'Publicar um imóvel leva poucos minutos.'
    },
    {
      icon: <Star color='secondary' />,
      title: 'Anúncios ilimitados',
      description: 'Você pode anunciar quantos imóveis quiser.'
    }
  ]

  const steps = [
    {
      number: '1',
      title: 'Crie sua conta',
      description: 'Informe seu nome, telefone, e-mail e crie sua senha.'
    },
    {
      number: '2',
      title: 'Preencha os dados do imóvel',
      description:
        'Adicione fotos, descrição, preço, características e localização aproximada.'
    },
    {
      number: '3',
      title: 'Publique',
      description: 'Seu anúncio entra no portal imediatamente.'
    },
    {
      number: '4',
      title: 'Receba contatos diretamente',
      description:
        'As mensagens chegam para você pelo WhatsApp, pelo formulário do portal ou por e-mail (opcional).'
    }
  ]

  const tips = [
    'Tire fotos claras e bem iluminadas',
    'Adicione detalhes completos',
    'Mantenha o preço atualizado',
    'Descreva pontos fortes do imóvel',
    'Revise tudo antes de publicar'
  ]

  const whoCanAdvertise = [
    'Proprietários individuais',
    'Família que deseja vender imóvel próprio',
    'Investidores particulares',
    'Pessoas com imóveis para revenda'
  ]

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', py: 6 }}>
      <Container maxWidth='lg'>
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography
            variant='h3'
            sx={{ fontWeight: 700, color: 'text.primary', mb: 2 }}
          >
            Para Proprietários – Anuncie Seu Imóvel
          </Typography>
          <Typography
            variant='h6'
            sx={{
              color: 'text.secondary',
              maxWidth: '900px',
              mx: 'auto',
              lineHeight: 1.6
            }}
          >
            A umarkt permite que qualquer proprietário anuncie seu imóvel de
            forma simples, gratuita e com alta visibilidade.
          </Typography>
          <Typography
            variant='body1'
            sx={{
              color: 'text.secondary',
              maxWidth: '900px',
              mx: 'auto',
              mt: 2,
              lineHeight: 1.6
            }}
          >
            Nossa plataforma foi criada para ajudar você a divulgar sua casa,
            apartamento, terreno ou imóvel comercial diretamente para quem está
            procurando.
          </Typography>
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              justifyContent: 'center',
              mt: 3,
              flexWrap: 'wrap'
            }}
          >
            <Chip
              label='Sem comissão'
              color='success'
              sx={{ fontWeight: 600, fontSize: '0.9rem', py: 2.5 }}
            />
            <Chip
              label='Sem burocracia'
              color='primary'
              sx={{ fontWeight: 600, fontSize: '0.9rem', py: 2.5 }}
            />
            <Chip
              label='Sem complicação'
              color='secondary'
              sx={{ fontWeight: 600, fontSize: '0.9rem', py: 2.5 }}
            />
          </Box>
        </Box>

        {/* Por que anunciar aqui */}
        <Box sx={{ mb: 6 }}>
          <Typography
            variant='h4'
            sx={{
              fontWeight: 700,
              mb: 4,
              color: 'text.primary',
              textAlign: 'center'
            }}
          >
            Por que anunciar seu imóvel aqui?
          </Typography>
          <Grid container spacing={3}>
            {benefits.map((benefit, index) => (
              <Grid size={{ xs: 12, md: 6 }} key={index}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    height: '100%',
                    borderRadius: 3,
                    border: '1px solid',
                    borderColor: 'grey.200',
                    transition: 'all 0.3s',
                    '&:hover': {
                      borderColor: 'primary.main',
                      transform: 'translateY(-4px)',
                      boxShadow: '0px 8px 24px rgba(255, 127, 39, 0.15)'
                    }
                  }}
                >
                  <Box
                    sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}
                  >
                    <Box
                      sx={{
                        width: 50,
                        height: 50,
                        borderRadius: '50%',
                        bgcolor: 'primary.light',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        color: 'primary.main'
                      }}
                    >
                      {React.cloneElement(benefit.icon, {
                        sx: { fontSize: '1.5rem' }
                      })}
                    </Box>
                    <Box>
                      <Typography
                        variant='h6'
                        sx={{
                          fontWeight: 600,
                          mb: 1,
                          color: 'text.primary',
                          fontSize: '1rem'
                        }}
                      >
                        {benefit.title}
                      </Typography>
                      <Typography
                        variant='body2'
                        sx={{ color: 'text.secondary', lineHeight: 1.7 }}
                      >
                        {benefit.description}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>

        <Divider sx={{ my: 6 }} />

        {/* Como funciona para proprietários */}
        <Box sx={{ mb: 6 }}>
          <Typography
            variant='h4'
            sx={{
              fontWeight: 700,
              mb: 4,
              color: 'text.primary',
              textAlign: 'center'
            }}
          >
            Como funciona para proprietários
          </Typography>
          <Paper elevation={0} sx={{ p: 4, bgcolor: 'white', borderRadius: 3 }}>
            <Grid container spacing={4}>
              {steps.map((step, index) => (
                <Grid size={{ xs: 12 }} key={index}>
                  <Box
                    sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}
                  >
                    <Chip
                      label={step.number}
                      sx={{
                        bgcolor: 'primary.main',
                        color: 'white',
                        fontWeight: 700,
                        fontSize: '1.2rem',
                        height: 50,
                        width: 50,
                        borderRadius: '50%'
                      }}
                    />
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant='h5'
                        sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}
                      >
                        {step.title}
                      </Typography>
                      <Typography
                        variant='body1'
                        sx={{ color: 'text.secondary', lineHeight: 1.7 }}
                      >
                        {step.description}
                      </Typography>
                    </Box>
                  </Box>
                  {index < steps.length - 1 && <Divider sx={{ mt: 3 }} />}
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Box>

        <Divider sx={{ my: 6 }} />

        {/* Dicas para ter mais resultados */}
        <Box sx={{ mb: 6 }}>
          <Typography
            variant='h4'
            sx={{
              fontWeight: 700,
              mb: 3,
              color: 'text.primary',
              textAlign: 'center'
            }}
          >
            Dicas para ter mais resultados
          </Typography>
          <Paper
            elevation={0}
            sx={{
              p: 4,
              bgcolor: 'primary.main',
              color: 'white',
              borderRadius: 3
            }}
          >
            <Grid container spacing={2}>
              {tips.map((tip, index) => (
                <Grid size={{ xs: 12, sm: 6 }} key={index}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <CheckCircleOutline sx={{ fontSize: '1.5rem' }} />
                    <Typography variant='body1'>{tip}</Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
            <Box
              sx={{
                mt: 3,
                pt: 3,
                borderTop: '1px solid rgba(255,255,255,0.2)'
              }}
            >
              <Typography
                variant='body1'
                sx={{ fontWeight: 600, fontSize: '1.1rem' }}
              >
                Anúncios completos recebem mais destaque.
              </Typography>
            </Box>
          </Paper>
        </Box>

        <Divider sx={{ my: 6 }} />

        {/* Quem pode anunciar */}
        <Box sx={{ mb: 6 }}>
          <Typography
            variant='h4'
            sx={{
              fontWeight: 700,
              mb: 3,
              color: 'text.primary',
              textAlign: 'center'
            }}
          >
            Quem pode anunciar?
          </Typography>
          <Paper elevation={0} sx={{ p: 4, bgcolor: 'white', borderRadius: 3 }}>
            <Grid container spacing={2}>
              {whoCanAdvertise.map((type, index) => (
                <Grid size={{ xs: 12, sm: 6 }} key={index}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        bgcolor: 'success.main'
                      }}
                    />
                    <Typography
                      variant='body1'
                      sx={{ color: 'text.secondary' }}
                    >
                      {type}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
            <Box
              sx={{
                mt: 3,
                pt: 3,
                borderTop: '1px solid',
                borderColor: 'grey.200'
              }}
            >
              <Typography
                variant='h6'
                sx={{
                  fontWeight: 600,
                  color: 'text.primary',
                  textAlign: 'center'
                }}
              >
                Se o imóvel é seu, você pode anunciar.
              </Typography>
            </Box>
          </Paper>
        </Box>

        <Divider sx={{ my: 6 }} />

        {/* CTA - Anuncie agora */}
        <Paper
          elevation={0}
          sx={{
            p: 5,
            textAlign: 'center',
            bgcolor: 'secondary.main',
            color: 'white',
            borderRadius: 3
          }}
        >
          <Typography variant='h4' sx={{ fontWeight: 700, mb: 2 }}>
            Anuncie seu imóvel agora
          </Typography>
          <Typography variant='body1' sx={{ mb: 4, opacity: 0.9 }}>
            Comece a divulgar seu imóvel de forma gratuita e alcance milhares de
            compradores interessados.
          </Typography>
          <Button
            component={RouterLink}
            to='/registro'
            variant='contained'
            size='large'
            startIcon={<AddHome />}
            sx={{
              bgcolor: 'primary.main',
              color: 'white',
              px: 4,
              py: 1.5,
              fontSize: '1.1rem',
              fontWeight: 600,
              '&:hover': {
                bgcolor: 'primary.dark'
              }
            }}
          >
            Criar Conta e Anunciar
          </Button>
        </Paper>

        {/* Contato */}
        <Box sx={{ textAlign: 'center', mt: 6 }}>
          <Typography
            variant='h5'
            sx={{ fontWeight: 700, mb: 3, color: 'text.primary' }}
          >
            Precisa de ajuda?
          </Typography>
          <Grid container spacing={2} sx={{ maxWidth: '600px', mx: 'auto' }}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'grey.200'
                }}
              >
                <WhatsApp
                  sx={{ color: 'success.main', fontSize: '2rem', mb: 1 }}
                />
                <Typography
                  variant='subtitle2'
                  sx={{ fontWeight: 600, mb: 0.5 }}
                >
                  WhatsApp
                </Typography>
                <Typography variant='caption' sx={{ color: 'text.secondary' }}>
                  (11) 9 8139-6930
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'grey.200'
                }}
              >
                <Email
                  sx={{ color: 'primary.main', fontSize: '2rem', mb: 1 }}
                />
                <Typography
                  variant='subtitle2'
                  sx={{ fontWeight: 600, mb: 0.5 }}
                >
                  E-mail
                </Typography>
                <Typography variant='caption' sx={{ color: 'text.secondary' }}>
                  contato@umarkt.com.br
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'grey.200'
                }}
              >
                <Language
                  sx={{ color: 'info.main', fontSize: '2rem', mb: 1 }}
                />
                <Typography
                  variant='subtitle2'
                  sx={{ fontWeight: 600, mb: 0.5 }}
                >
                  Site
                </Typography>
                <Typography variant='caption' sx={{ color: 'text.secondary' }}>
                  www.umarkt.com.br
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      </Container>
    </Box>
  )
}

export default ForOwners
